/**
 * Skeleton loading placeholders
 * Shows content-shaped placeholders while data loads
 * 
 * Usage:
 *   <SkeletonCard /> - Card placeholder
 *   <SkeletonText lines={3} /> - Text lines
 *   <SkeletonImage /> - Image placeholder
 */

import React from 'react';

interface SkeletonProps {
  width?: string;
  height?: string;
  style?: React.CSSProperties;
}

// Base skeleton element
export function Skeleton({ width, height = '1rem', style }: SkeletonProps) {
  return (
    <div
      style={{
        width: width || '100%',
        height,
        backgroundColor: 'var(--color-card)',
        borderRadius: '6px',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(196, 255, 0, 0.1), transparent)',
          animation: 'shimmer 1.5s infinite',
        }}
      />
    </div>
  );
}

// Text skeleton (multiple lines)
interface SkeletonTextProps {
  lines?: number;
  lineHeight?: string;
}

export function SkeletonText({ lines = 3, lineHeight = '1rem' }: SkeletonTextProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          height={lineHeight}
          width={i === lines - 1 ? '80%' : '100%'}
        />
      ))}
    </div>
  );
}

// Card skeleton
export function SkeletonCard() {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Title */}
        <Skeleton height="1.5rem" width="60%" />
        {/* Content */}
        <SkeletonText lines={2} />
        {/* Meta info */}
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Skeleton height="1rem" width="100px" />
          <Skeleton height="1rem" width="80px" />
        </div>
      </div>
    </div>
  );
}

// Image skeleton
interface SkeletonImageProps {
  aspectRatio?: string;
  borderRadius?: string;
}

export function SkeletonImage({ 
  aspectRatio = '16/9',
  borderRadius = '8px' 
}: SkeletonImageProps) {
  return (
    <Skeleton
      height="0"
      style={{
        paddingBottom: `calc(100% / (${aspectRatio}))`,
        borderRadius,
      }}
    />
  );
}

// Check-in card skeleton
export function SkeletonCheckInCard() {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', gap: '1rem' }}>
        {/* Photo */}
        <Skeleton width="80px" height="80px" style={{ flexShrink: 0 }} />
        {/* Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Skeleton height="1.25rem" width="70%" />
          <Skeleton height="1rem" width="50%" />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Skeleton height="1rem" width="60px" />
            <Skeleton height="1rem" width="80px" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Event card skeleton
export function SkeletonEventCard() {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Title & icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Skeleton width="24px" height="24px" style={{ borderRadius: '50%' }} />
          <Skeleton height="1.5rem" width="60%" />
        </div>
        {/* Description */}
        <SkeletonText lines={2} lineHeight="0.875rem" />
        {/* Meta */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <Skeleton height="1rem" width="120px" />
          <Skeleton height="1rem" width="100px" />
        </div>
        {/* Button */}
        <Skeleton height="2.5rem" style={{ borderRadius: '999px' }} />
      </div>
    </div>
  );
}

// Add shimmer animation to CSS (will be added to index.css)
export const skeletonStyles = `
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
`;

